import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../Header';
import Footer from '../Footer';
import { Button } from '../ui/button';
import { BookOpen, Trophy, Star, ChevronRight, Loader2, ArrowLeft, Check, X, Award } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../lib/api';

const BibleQuizPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [difficulty, setDifficulty] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myResults, setMyResults] = useState([]);

  const getHeaders = () => {
    const t = localStorage.getItem('session_token');
    return t ? { 'Authorization': `Bearer ${t}` } : {};
  };

  const difficultyConfig = {
    easy: { color: 'emerald', label: 'Easy', desc: 'Great for beginners and young readers' },
    medium: { color: 'amber', label: 'Medium', desc: 'For those growing in their faith knowledge' },
    hard: { color: 'red', label: 'Hard', desc: 'Challenge yourself with deeper Bible knowledge' }
  };

  useEffect(() => {
    if (difficulty) {
      fetch(`${API_URL}/api/quiz/list?difficulty=${difficulty}`).then(r => r.json()).then(setQuizzes).catch(() => {});
      fetch(`${API_URL}/api/quiz/leaderboard?difficulty=${difficulty}`).then(r => r.json()).then(setLeaderboard).catch(() => {});
    }
  }, [difficulty]);

  useEffect(() => {
    if (isAuthenticated) {
      fetch(`${API_URL}/api/quiz/my-results`, { headers: getHeaders(), credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(data => setMyResults(Array.isArray(data) ? data : []))
        .catch(() => setMyResults([]));
    }
  }, [isAuthenticated]);

  const fetchWithAuth = async (url, options = {}) => {
    const r = await fetch(url, {
      ...options,
      headers: { ...getHeaders(), ...(options.headers || {}) },
      credentials: 'include'
    });
    if (r.status === 401) {
      toast.error('Please sign in to continue');
      navigate('/login');
      return null;
    }
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || 'Something went wrong');
      return null;
    }
    return r;
  };

  const startQuiz = async (quizId) => {
    if (!isAuthenticated) { toast.error('Please sign in to take quizzes'); navigate('/login'); return; }
    setLoading(true);
    try {
      const r = await fetchWithAuth(`${API_URL}/api/quiz/${quizId}`);
      if (r) { setActiveQuiz(await r.json()); setCurrentQ(0); setAnswers({}); setResult(null); }
    } catch (e) { toast.error('Failed to load quiz'); }
    setLoading(false);
  };

  const selectAnswer = (qIdx, ansIdx) => {
    setAnswers({ ...answers, [qIdx]: ansIdx });
  };

  const submitQuiz = async () => {
    if (!activeQuiz) return;
    if (!isAuthenticated) { toast.error('Please sign in to submit'); navigate('/login'); return; }
    setLoading(true);
    try {
      const r = await fetchWithAuth(`${API_URL}/api/quiz/${activeQuiz.quiz_id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers })
      });
      if (r) {
        const data = await r.json();
        setResult(data);
        if (data.score_percent >= 80) toast.success('Great job!');
      }
    } catch (e) { toast.error('Failed to submit quiz'); }
    setLoading(false);
  };

  // Difficulty selection screen
  if (!difficulty) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-amber-50">
        <Header />
        <main className="pt-24 pb-16 max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-4">
              <BookOpen className="w-4 h-4" /><span className="text-sm font-medium">Bible Quiz</span>
            </div>
            <h1 className="text-4xl font-serif font-bold text-slate-800 mb-4">Test Your Bible Knowledge</h1>
            <p className="text-lg text-slate-600">Choose your difficulty level and see how much you know!</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(difficultyConfig).map(([key, cfg]) => (
              <button key={key} onClick={() => setDifficulty(key)} className={`bg-white rounded-2xl border-2 border-${cfg.color}-200 p-8 text-center hover:shadow-xl transition-all hover:scale-105 hover:border-${cfg.color}-400`}>
                <div className={`w-16 h-16 bg-${cfg.color}-100 rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <Star className={`w-8 h-8 text-${cfg.color}-600`} />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">{cfg.label}</h3>
                <p className="text-sm text-slate-500">{cfg.desc}</p>
              </button>
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Active quiz
  if (activeQuiz && !result) {
    const q = activeQuiz.questions[currentQ];
    const total = activeQuiz.questions.length;
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex flex-col">
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
          <button onClick={() => { setActiveQuiz(null); setAnswers({}); }} className="text-slate-500 hover:text-slate-700 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Exit</button>
          <span className="font-bold text-slate-800">{activeQuiz.title}</span>
          <span className="text-sm text-slate-500">{currentQ + 1}/{total}</span>
        </div>
        <div className="w-full bg-slate-100 h-2"><div className="h-full bg-purple-500 transition-all" style={{ width: `${((currentQ + 1) / total) * 100}%` }} /></div>
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-xl w-full">
            <h2 className="text-xl font-bold text-slate-800 mb-6 text-center">{q.question}</h2>
            <div className="space-y-3">
              {q.options.map((opt, i) => (
                <button key={i} onClick={() => selectAnswer(currentQ, i)} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${answers[currentQ] === i ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' : 'border-slate-200 hover:border-purple-300 bg-white'}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${answers[currentQ] === i ? 'bg-purple-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="font-medium text-slate-700">{opt}</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-8">
              <Button variant="outline" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}>Previous</Button>
              {currentQ < total - 1 ? (
                <Button onClick={() => setCurrentQ(currentQ + 1)} disabled={answers[currentQ] === undefined} className="bg-purple-500 hover:bg-purple-600 text-white">Next</Button>
              ) : (
                <Button onClick={submitQuiz} disabled={Object.keys(answers).length < total || loading} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />} Submit Quiz
                </Button>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Result screen
  if (result) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex flex-col">
        <Header />
        <main className="flex-1 pt-24 pb-16 max-w-2xl mx-auto px-4 text-center">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${result.score_percent >= 80 ? 'bg-emerald-100' : result.score_percent >= 50 ? 'bg-amber-100' : 'bg-red-100'}`}>
            <Award className={`w-12 h-12 ${result.score_percent >= 80 ? 'text-emerald-600' : result.score_percent >= 50 ? 'text-amber-600' : 'text-red-600'}`} />
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">
            {result.score_percent >= 80 ? 'Amazing!' : result.score_percent >= 50 ? 'Good effort!' : 'Keep learning!'}
          </h1>
          <p className="text-5xl font-bold text-purple-600 mb-2">{result.score_percent}%</p>
          <p className="text-slate-500 mb-8">{result.score} out of {result.total} correct</p>
          
          <div className="space-y-3 text-left mb-8">
            {result.results.map((r, i) => (
              <div key={i} className={`p-4 rounded-xl border ${r.is_correct ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-start gap-2">
                  {r.is_correct ? <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" /> : <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{r.question}</p>
                    {!r.is_correct && <p className="text-xs text-red-600 mt-1">Correct: {activeQuiz.questions[i].options[r.correct_answer]}</p>}
                    {r.explanation && <p className="text-xs text-slate-500 mt-1">{r.explanation}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => { setActiveQuiz(null); setResult(null); setAnswers({}); }}>Back to Quizzes</Button>
            <Button onClick={() => { setCurrentQ(0); setAnswers({}); setResult(null); }} className="bg-purple-500 hover:bg-purple-600 text-white">Try Again</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Quiz list
  const cfg = difficultyConfig[difficulty];
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-amber-50">
      <Header />
      <main className="pt-24 pb-16 max-w-4xl mx-auto px-4">
        <button onClick={() => setDifficulty(null)} className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-6"><ArrowLeft className="w-4 h-4" /> All Difficulties</button>
        <h1 className="text-3xl font-serif font-bold text-slate-800 mb-2">{cfg.label} Quizzes</h1>
        <p className="text-slate-500 mb-8">{cfg.desc}</p>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {quizzes.map(q => {
            const myBest = myResults.find(r => r.quiz_id === q.quiz_id);
            return (
              <button key={q.quiz_id} onClick={() => startQuiz(q.quiz_id)} className="bg-white rounded-xl border p-5 text-left hover:shadow-lg transition-all hover:border-purple-300 group">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-purple-600">Quiz {q.quiz_number}</span>
                  {myBest && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${myBest.score_percent >= 80 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{myBest.score_percent}%</span>}
                </div>
                <h3 className="font-bold text-slate-800 group-hover:text-purple-600 transition-colors">{q.title}</h3>
                <p className="text-xs text-slate-500 mt-1">{q.question_count} questions</p>
                <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-purple-500 mt-2 transition-colors" />
              </button>
            );
          })}
        </div>

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="bg-white rounded-2xl border p-6">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Leaderboard</h3>
            <div className="space-y-2">
              {leaderboard.map((l, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-slate-400 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-200 text-slate-600'}`}>{i + 1}</span>
                    <span className="font-medium text-slate-800">{l.user_name}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-purple-600">{l.best_score}%</span>
                    <span className="text-xs text-slate-400 ml-2">{l.quizzes_completed} quizzes</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BibleQuizPage;
